
import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import RegistrationGuard from "@/components/auth/RegistrationGuard";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import PsychologistDisplayCard from "@/components/schedule/PsychologistDisplayCard";
import AppointmentSummaryCard from "@/components/schedule/AppointmentSummaryCard";
import ScheduleStep2Header from "@/components/schedule/ScheduleStep2Header";
import NewSchedulingSystem from "@/components/schedule/NewSchedulingSystem";

interface PsychologistData {
  id: string;
  name: string;
  photo_url?: string;
  crp: string;
  rating?: number;
  specialties: string[];
}

const ScheduleStep2 = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [psychologistId, setPsychologistId] = useState<string | null>(null);
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [selectedTime, setSelectedTime] = useState<string>('');
  const [selectedDuration, setSelectedDuration] = useState<number>(0);
  const [selectedPrice, setSelectedPrice] = useState<number>(0);
  const [error, setError] = useState("");

  // Buscar dados do psicólogo
  const { data: psychologist, isLoading: isPsychologistLoading } = useQuery({
    queryKey: ['psychologist-details', psychologistId],
    queryFn: async (): Promise<PsychologistData> => {
      console.log('👨‍⚕️ [DEBUG] Buscando dados do psicólogo:', psychologistId);
      
      if (!psychologistId) {
        throw new Error('ID do psicólogo não fornecido');
      }
      
      const { data, error } = await supabase
        .from('psychologists')
        .select('*')
        .eq('id', psychologistId)
        .single();
      
      if (error) {
        console.error('❌ [ERROR] Erro ao buscar psicólogo:', error);
        throw new Error(`Erro ao buscar psicólogo: ${error.message}`);
      }
      
      return {
        id: data.id,
        name: data.name,
        photo_url: data.photo_url,
        crp: data.crp,
        rating: data.rating,
        specialties: data.specialties || []
      };
    },
    enabled: !!psychologistId,
    retry: 2,
    retryDelay: 1000
  });

  // Verificar psicólogo selecionado
  useEffect(() => {
    const urlPsychologistId = searchParams.get('psychologistId');
    const sessionPsychologistId = sessionStorage.getItem('selectedPsychologist');
    
    const selectedPsychologistId = urlPsychologistId || sessionPsychologistId;
    
    if (!selectedPsychologistId) {
      toast.error('Nenhum psicólogo selecionado');
      navigate('/schedule/step1');
      return;
    }
    
    // Validar formato UUID
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    if (!uuidRegex.test(selectedPsychologistId)) {
      toast.error('ID do psicólogo inválido');
      navigate('/schedule/step1');
      return;
    }
    
    setPsychologistId(selectedPsychologistId);
    
    if (urlPsychologistId) {
      sessionStorage.setItem('selectedPsychologist', urlPsychologistId);
    }
  }, [navigate, searchParams]);

  const handleBack = () => {
    if (psychologistId) {
      navigate(`/schedule/step1?psychologistId=${psychologistId}`);
    } else {
      navigate('/schedule/step1');
    }
  };

  const handleSlotSelect = (date: string, time: string, duration: number, price: number) => {
    setSelectedDate(date);
    setSelectedTime(time);
    setSelectedDuration(duration);
    setSelectedPrice(price);
    setError("");
    
    toast.success(`Horário selecionado: ${time.substring(0, 5)} - ${duration} minutos`);
  };

  const handleNext = () => {
    if (!selectedDate || !selectedTime || !selectedDuration) {
      const message = "Por favor, selecione data, horário e duração da consulta.";
      setError(message);
      toast.warning(message);
      return;
    }
    
    // Salvar dados na sessão
    sessionStorage.setItem('selectedDate', selectedDate);
    sessionStorage.setItem('selectedTime', selectedTime);
    sessionStorage.setItem('duration', selectedDuration.toString());
    sessionStorage.setItem('price', selectedPrice.toString());
    
    navigate('/schedule/step3');
  };

  const canProceed = () => {
    return selectedDate && selectedTime && selectedDuration > 0;
  };

  if (isPsychologistLoading) {
    return (
      <RegistrationGuard>
        <div className="space-y-8 animate-fade-in">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-1/3 mb-4"></div>
            <div className="h-64 bg-gray-200 rounded mb-6"></div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="h-32 bg-gray-200 rounded"></div>
              ))}
            </div>
          </div>
        </div>
      </RegistrationGuard>
    );
  }

  if (!psychologist) {
    return (
      <RegistrationGuard>
        <div className="space-y-8">
          <div className="p-6 text-center">
            <h1 className="text-2xl font-bold mb-4">Psicólogo não encontrado</h1>
            <p className="text-muted-foreground mb-4">
              O psicólogo solicitado não foi encontrado.
            </p>
            <Button onClick={handleBack}>
              Voltar à seleção
            </Button>
          </div>
        </div>
      </RegistrationGuard>
    );
  }

  return (
    <RegistrationGuard>
      <div className="space-y-8 animate-fade-in">
        <ScheduleStep2Header psychologistName={psychologist.name} />

        <PsychologistDisplayCard psychologist={psychologist} />

        <NewSchedulingSystem
          psychologistId={psychologistId!}
          psychologistName={psychologist.name}
          onSelectSlot={handleSlotSelect}
        />

        {/* Resumo da consulta */}
        {canProceed() && (
          <AppointmentSummaryCard
            psychologist={psychologist}
            selectedDate={selectedDate}
            selectedTime={selectedTime}
            selectedDuration={selectedDuration}
            pricing={{
              price_30min: selectedDuration === 30 ? selectedPrice : 150,
              price_60min: selectedDuration === 60 ? selectedPrice : 280
            }}
          />
        )}

        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        
        <div className="flex justify-between">
          <Button variant="outline" onClick={handleBack}>
            <ArrowLeft className="mr-2 h-4 w-4" /> Voltar
          </Button>
          <Button 
            onClick={handleNext} 
            disabled={!canProceed()}
            className={`${canProceed() ? 'bg-cyan-500 hover:bg-cyan-600' : 'bg-gray-400 cursor-not-allowed'}`}
          >
            Próximo <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </div>
    </RegistrationGuard>
  );
};

export default ScheduleStep2;
